import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Button } from '../common/Button';
import { compactName } from '../../utils/playerName';
import type { CompetitionMatch } from '../../types';

const VALID_SCORES = new Set([
  '7-6', '6-7', '7-5', '5-7', '6-4', '4-6', '6-3', '3-6',
  '6-2', '2-6', '6-1', '1-6', '6-0', '0-6',
]);

// Tight space (especially under each stepper) → first-name + last-initial.
function teamLabel(team: CompetitionMatch['team1']): string {
  if (!team) return 'A definir';
  const names = team.players.map((p) => compactName(p.name) || '?');
  return names.join(' & ');
}

interface MatchResultModalProps {
  match: CompetitionMatch | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmitScore: (
    matchId: string,
    data: { team1Games: number; team2Games: number; tiebreakScore?: number | null }
  ) => Promise<void>;
  onSubmitWO: (matchId: string, winnerTeamId: string) => Promise<void>;
}

/**
 * Admin result-entry modal shared by the group standings view and the bracket.
 * Single set, games 0–7, tiebreak required on 7×6. Supports W.O. entry.
 */
export function MatchResultModal({
  match,
  isOpen,
  onClose,
  onSubmitScore,
  onSubmitWO,
}: MatchResultModalProps) {
  const [mode, setMode] = useState<'score' | 'wo'>('score');
  const [team1Games, setTeam1Games] = useState(0);
  const [team2Games, setTeam2Games] = useState(0);
  const [tiebreak, setTiebreak] = useState('');
  const [woWinner, setWoWinner] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && match) {
      setMode('score');
      setTeam1Games(match.team1Games ?? 0);
      setTeam2Games(match.team2Games ?? 0);
      setTiebreak(match.tiebreakScore != null ? String(match.tiebreakScore) : '');
      setWoWinner(match.winnerTeamId ?? null);
      setError(null);
    }
  }, [isOpen, match]);

  if (!match) return null;

  const isTiebreak =
    (team1Games === 7 && team2Games === 6) || (team1Games === 6 && team2Games === 7);

  const handleSave = async () => {
    if (!match.team1 || !match.team2) {
      setError('A partida ainda não tem as duas duplas definidas');
      return;
    }
    setError(null);
    setIsSaving(true);
    try {
      if (mode === 'wo') {
        if (!woWinner) {
          setError('Selecione a dupla vencedora');
          setIsSaving(false);
          return;
        }
        await onSubmitWO(match.id, woWinner);
      } else {
        const key = `${team1Games}-${team2Games}`;
        if (!VALID_SCORES.has(key)) {
          setError('Placar inválido. Use 6x0, 6x1, 6x2, 6x3, 6x4, 7x5 ou 7x6.');
          setIsSaving(false);
          return;
        }
        if (isTiebreak && !tiebreak.trim()) {
          setError('Informe os pontos do perdedor no tie-break (ex: 4).');
          setIsSaving(false);
          return;
        }
        await onSubmitScore(match.id, {
          team1Games,
          team2Games,
          tiebreakScore: isTiebreak ? parseInt(tiebreak, 10) : null,
        });
      }
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Erro ao salvar resultado');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm glass-card rounded-2xl p-6"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-display text-lg font-bold text-white">Registrar resultado</h3>
              <button
                onClick={onClose}
                className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="text-center text-sm text-white/70 mb-4">
              <span className="font-semibold text-white">{teamLabel(match.team1)}</span>
              <span className="mx-2 text-white/40">vs</span>
              <span className="font-semibold text-white">{teamLabel(match.team2)}</span>
            </div>

            {/* Mode toggle */}
            <div className="flex gap-2 mb-4 p-1 rounded-xl bg-white/5">
              {(['score', 'wo'] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                    mode === m ? 'bg-turquoise/20 text-turquoise' : 'text-white/50 hover:text-white'
                  }`}
                >
                  {m === 'score' ? 'Placar' : 'W.O.'}
                </button>
              ))}
            </div>

            {mode === 'score' ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-4">
                  <ScoreStepper
                    label={teamLabel(match.team1)}
                    value={team1Games}
                    onChange={setTeam1Games}
                  />
                  <span className="text-white/40 text-xl font-bold mt-6">×</span>
                  <ScoreStepper
                    label={teamLabel(match.team2)}
                    value={team2Games}
                    onChange={setTeam2Games}
                  />
                </div>
                {isTiebreak && (
                  <div>
                    <label className="block text-sm text-white/60 mb-2">
                      Pontos do perdedor no tie-break
                    </label>
                    <input
                      type="number"
                      value={tiebreak}
                      onChange={(e) => setTiebreak(e.target.value)}
                      placeholder="Ex: 4"
                      className="w-full px-4 py-2.5 rounded-xl bg-white/10 border border-white/10 text-white text-center focus:outline-none focus:border-turquoise/50"
                    />
                    <p className="text-[11px] text-white/40 mt-1.5 text-center">
                      Ex.: tie-break 7×4 → informe 4. Aparece como 6(4).
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-white/60 mb-2">Quem venceu por W.O.?</p>
                {[match.team1, match.team2].map((team) =>
                  team ? (
                    <button
                      key={team.id}
                      onClick={() => setWoWinner(team.id)}
                      className={`w-full py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                        woWinner === team.id
                          ? 'bg-turquoise/20 border-turquoise/50 text-turquoise'
                          : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
                      }`}
                    >
                      {teamLabel(team)}
                    </button>
                  ) : null
                )}
              </div>
            )}

            {error && <p className="text-coral text-sm mt-4">{error}</p>}

            <Button onClick={handleSave} fullWidth isLoading={isSaving} className="mt-5">
              Salvar resultado
            </Button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ScoreStepper({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-[11px] text-white/50 max-w-[90px] truncate text-center">{label}</span>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onChange(Math.max(0, value - 1))}
          className="w-7 h-7 rounded-lg bg-white/10 text-white/70 hover:bg-white/20 flex items-center justify-center"
        >
          −
        </button>
        <span className="w-8 text-center text-xl font-bold text-white">{value}</span>
        <button
          onClick={() => onChange(Math.min(7, value + 1))}
          className="w-7 h-7 rounded-lg bg-white/10 text-white/70 hover:bg-white/20 flex items-center justify-center"
        >
          +
        </button>
      </div>
    </div>
  );
}
